import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorPanel, SkeletonSet } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { LoadFailure } from '../LoadFailure.js';
import { escalation, type EscalationOutcome } from './escalationClient.js';
import {
  escalationDirectoryState,
  escalationSentence,
  outcomeCellLabel,
  personLabel,
  queueScopeSentence,
  ruleSentence,
} from './escalationLogic.js';

/**
 * ===========================================================================
 * ESCALATIONS WAITING ON MANAGEMENT — the three non-routed answers, kept apart.
 * ===========================================================================
 *
 * `GET /api/insights/escalations/open` defaults to the three outcomes that are
 * not `routed`, and this screen keeps them apart rather than merging them into
 * a single "needs attention" pile. That is not tidiness. The three have three
 * different remedies and three different people who can apply them:
 *
 *   Nobody configured  → an administrator configures an escalation target.
 *   Named, cannot act  → somebody grants access, or names a different person.
 *   More than one      → a person chooses, on the finding itself.
 *
 * A merged queue would send every one of those to the same wrong screen. The
 * filter therefore offers the three separately AND together, and the counts
 * are per-outcome so an organization can see that it has, say, forty of the
 * first and none of the other two — which is a configuration problem with one
 * fix, and reads as forty unrelated incidents in a merged list.
 *
 * ---------------------------------------------------------------------------
 * THE COUNT IS NOT YOURS, AND THE SCREEN SAYS SO
 * ---------------------------------------------------------------------------
 * The server counts the ORGANIZATION's open escalations for the requested
 * outcomes, then removes the rows whose findings this reader does not reach —
 * narrowing AFTER the count, and returning `reachNarrowed` to disclose it.
 * Rendering `total` as a headline would be the "scope applied after
 * pagination" defect restated on screen; rendering only the visible count
 * would hide that there is more and nobody is looking at it. So both numbers
 * appear, and `queueScopeSentence` says which is which.
 */

const INSIGHTS_READ_KEY = 'insights:read';
const DIRECTORY_READ_KEY = 'directory:read';

const OUTCOMES: readonly EscalationOutcome[] = ['ambiguous', 'unconfigured', 'unauthorized'];

/** What each filter is FOR, in the language of the remedy it needs. */
const FILTER_LABEL: Record<EscalationOutcome, string> = {
  ambiguous: 'More than one owner — somebody has to choose',
  unconfigured: 'Nobody configured — an escalation target has to be set up',
  unauthorized: 'Named but cannot act — access has to be granted',
  routed: 'Routed',
};

export const ESCALATIONS_IS =
  'The escalation queue is every escalation that resolved to nobody: findings where no owner is configured, where the people your records name may not act on them, and where more than one person is equally valid and Phibian refused to choose. Each one is waiting on a person, and the three need three different things done.';

export function EscalationQueue() {
  const permissions = usePermissions();
  const mayRead = gateOpen(authorityGate(permissions, INSIGHTS_READ_KEY));
  const directoryGate = authorityGate(permissions, DIRECTORY_READ_KEY);
  const mayReadDirectory = gateOpen(directoryGate);
  const directoryState = escalationDirectoryState(directoryGate);
  const [filter, setFilter] = useState<EscalationOutcome | 'all'>('all');

  /*
   * NO DIRECTORY QUERY. The queue used to fetch `directory.workers()` and
   * resolve prior-owner ids against it; the server now sends the disclosure
   * answer with the person, so the client no longer holds a second, weaker
   * opinion about who this reader may be told about. The `directory:read` gate
   * above is unchanged — reading a queue that names people is still reading
   * the workforce, and that is a permission question, not a data one.
   */
  const queue = useQuery({
    queryKey: ['insights', 'escalations', 'open', filter],
    queryFn: () => escalation.open(filter === 'all' ? {} : { outcome: [filter] }),
    enabled: mayRead && mayReadDirectory,
  });

  if (directoryState === 'unknown') {
    // NOT a refusal, and must not be worded as one (PH-ADM-002). The set that
    // decides `directory:read` could not be read — a dropped connection or a
    // failed background refresh — so the truthful sentence is "we could not
    // check", with the retry that fixes it, exactly as `RequirePermission`
    // and `authorityGate.gateSentence` already say it elsewhere in this app.
    return (
      <ErrorPanel
        message="We couldn't check whether you have workforce directory access. This is a connection problem, not a decision about your account — nothing here has been withheld from you."
        onRetry={permissions.refetch}
      />
    );
  }

  if (directoryState === 'refused') {
    return (
      <section className="rn-card">
        <h1>Escalations waiting</h1>
        <p className="rn-muted" data-escalation-queue-refused>
          An escalation names people, so reading this queue is reading the workforce. You do not
          hold directory access. This is a refusal — it does not mean there is nothing here.
        </p>
      </section>
    );
  }

  if (queue.isLoading) return <SkeletonSet surface="table" rows={6} />;
  if (queue.isError) {
    return (
      <LoadFailure
        error={queue.error}
        message="We could not load the escalations that are waiting."
        onRetry={() => void queue.refetch()}
        refusedHeading="You cannot see this organization’s escalations"
      />
    );
  }

  const page = queue.data;
  if (!page) return null;

  return (
    <article className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <a className="rn-link" href="/insights">
        ← Insights
      </a>
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>Escalations waiting</h1>
        <p className="rn-muted">
          Every escalation here reached nobody. Phibian recorded it rather than dropping it, and it
          stays until a person acts.
        </p>
        <p className="rn-muted" data-escalation-queue-scope>
          {queueScopeSentence(page, page.items.length)}
        </p>
      </header>

      <nav className="rn-row" style={{ gap: 'var(--space-8)', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="rn-btn-quiet"
          aria-pressed={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All three
        </button>
        {OUTCOMES.map((outcome) => (
          <button
            key={outcome}
            type="button"
            className="rn-btn-quiet"
            aria-pressed={filter === outcome}
            data-escalation-filter={outcome}
            onClick={() => setFilter(outcome)}
          >
            {FILTER_LABEL[outcome]}
          </button>
        ))}
      </nav>

      {page.items.length === 0 ? (
        <p className="rn-muted" data-escalation-queue-empty>
          {page.reachNarrowed
            ? 'Nothing here that your access covers. That is not the same as nothing in this organization — the count above says how many there are.'
            : 'Nothing is waiting. Every escalation this organization has recorded reached somebody.'}
        </p>
      ) : (
        <ul className="rn-list">
          {page.items.map((record) => {
            const sentence = escalationSentence(record);
            return (
              <li
                key={record.id}
                className="rn-col"
                data-escalation-row={record.outcome}
                data-escalation-reason={sentence.reason}
              >
                <a className="rn-link" href={`/insights/${record.findingId}`}>
                  {outcomeCellLabel(record.outcome)} —{' '}
                  <time dateTime={record.escalatedAt}>
                    {new Date(record.escalatedAt).toLocaleString()}
                  </time>
                </a>
                <span className="rn-strong">{sentence.headline}</span>
                <span className="rn-muted">{sentence.remedy}</span>
                <span className="rn-muted">{ruleSentence(record.resolutionRule)}</span>
                <span
                  className="rn-muted"
                  data-escalation-row-prior-owner={personLabel(record.priorOwner).disclosure}
                >
                  Escalated past{' '}
                  {record.priorOwner === null
                    ? 'nobody — this finding had no owner'
                    : personLabel(record.priorOwner).label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}
